import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PnLFilters from "./PnLFilters";
import PnLDetailDrawer from "./PnLDetailDrawer";
import PnLRefreshModal from "./PnLRefreshModal";
import { PnLKpis, PnLLegend, PnLResults } from "./PnLSummaryParts";
import { useRefreshFlow, useSummaryData } from "./usePnlSummary";
import {
  applyFilterChange,
  buildCsv,
  buildCsvFilename,
  downloadCsv,
  filterRows,
  formatLastRefreshed,
  getCategoryOptions,
  getDefaultFilters,
  getOpenMonths,
  getTypeOptions,
  groupRowsByMonth,
  isPnlReportUser,
  sumTotals,
  toggleKey,
  validateFilters,
} from "./pnlReportUtils";
import "./pnlReport.css";

const PnLSummaryReport = () => {
  const navigate = useNavigate();
  const userDetails = useSelector((state) => state?.auth?.user);
  const canView = isPnlReportUser(userDetails?.Value);

  const [filters, setFilters] = useState(() => getDefaultFilters());
  const [filterError, setFilterError] = useState("");
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());
  const [selectedRow, setSelectedRow] = useState(null);

  const { appliedFilters, rows, loading, loadError, loadReport } = useSummaryData(canView);

  const visibleRows = useMemo(
    () => filterRows(rows, appliedFilters.category, appliedFilters.subType),
    [rows, appliedFilters.category, appliedFilters.subType]
  );
  const groups = useMemo(() => groupRowsByMonth(visibleRows), [visibleRows]);
  const totals = useMemo(() => sumTotals(visibleRows), [visibleRows]);
  const openMonths = useMemo(() => getOpenMonths(visibleRows), [visibleRows]);
  const categoryOptions = useMemo(() => getCategoryOptions(rows), [rows]);
  const typeOptions = useMemo(() => getTypeOptions(rows, filters.category), [rows, filters.category]);

  const { showRefresh, setShowRefresh, refreshing, lastRefreshed, confirmRefresh } = useRefreshFlow({
    openMonths,
    appliedFilters,
    loadReport,
  });

  if (!canView) return <Navigate to="/reports" replace />;

  const handleFilterChange = (field, value) => {
    setFilters((previous) => applyFilterChange(previous, field, value));
    setFilterError("");
  };

  const handleApply = () => {
    const message = validateFilters(filters);
    setFilterError(message);
    if (!message) void loadReport(filters);
  };

  const handleClear = () => {
    const defaults = getDefaultFilters();
    setFilters(defaults);
    setFilterError("");
    setCollapsedKeys(new Set());
    void loadReport(defaults);
  };

  const handleToggleMonth = (key) => setCollapsedKeys((previous) => toggleKey(previous, key));

  const handleToggleAll = () => {
    setCollapsedKeys(collapsedKeys.size ? new Set() : new Set(groups.map((group) => group.key)));
  };

  const handleExport = () => {
    if (!groups.length) {
      toast.info("There is nothing to export for the selected filters.");
      return;
    }
    downloadCsv(buildCsvFilename(appliedFilters), buildCsv(groups, totals));
  };

  return (
    <main className="pnl-report">
      <div className="main__container">
        <div className="main__tile_heading cus-btn-include-sec zIndex3">
          <span>Profit &amp; Loss Summary</span>
          <div className="top_right_button_container">
            <div className="pnl-last-refreshed">
              Last refreshed<br />
              <strong>{formatLastRefreshed(lastRefreshed)}</strong>
            </div>
            <button type="button" className="btn cus-seconday-bg-btn mr-2" onClick={() => setShowRefresh(true)} disabled={loading}>
              Refresh
            </button>
            <button type="button" className="btn cus-seconday-bg-btn mr-2" onClick={handleExport} disabled={loading}>
              Export CSV
            </button>
            <button type="button" className="btn cus-primary-transparent-btn" onClick={() => navigate("/reports")}>
              Back
            </button>
          </div>
        </div>
        <p className="pnl-subtitle">Monthly gross and net by category, read from stored report snapshots.</p>

        <PnLFilters
          filters={filters}
          categoryOptions={categoryOptions}
          typeOptions={typeOptions}
          errorMessage={filterError}
          disabled={loading}
          onChange={handleFilterChange}
          onApply={handleApply}
          onClear={handleClear}
        />
        <PnLKpis totals={totals} />
        <PnLLegend allCollapsed={collapsedKeys.size > 0} disabled={!groups.length} onToggleAll={handleToggleAll} />
        <PnLResults
          loading={loading}
          loadError={loadError}
          groups={groups}
          totals={totals}
          collapsedKeys={collapsedKeys}
          onToggleMonth={handleToggleMonth}
          onSelectRow={setSelectedRow}
        />
      </div>

      {selectedRow && <PnLDetailDrawer row={selectedRow} onClose={() => setSelectedRow(null)} />}
      <PnLRefreshModal
        show={showRefresh}
        openMonths={openMonths}
        refreshing={refreshing}
        onCancel={() => setShowRefresh(false)}
        onConfirm={confirmRefresh}
      />
    </main>
  );
};

export default PnLSummaryReport;
